import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2, Users, Calendar, CheckCircle2, XCircle } from "lucide-react";
import { format } from "date-fns";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";

interface TenantWithUserCount {
  id: string;
  name: string;
  slug: string;
  planType: string;
  currentPlanId: string | null;
  maxUsers: number;
  maxGoals: number;
  isActive: boolean;
  createdAt: string;
  userCount: number;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  price: number;
  interval: string;
}

export default function ManageTenantsPage() {
  const { isPlatformOwner, hasPermission, isLoading: permissionsLoading } = usePlatformPermissions();
  const { toast } = useToast();
  // Any operational platform permission can require tenant context. A delegate
  // can inspect the tenant list, but can only change plans with plan.assign.
  const canViewTenants = isPlatformOwner || hasPermission(
    "tenant.provision", "user.invite", "billing.view", "billing.manage", "plan.assign"
  );
  const canManageTenants = isPlatformOwner || hasPermission("plan.assign");
  const canProvisionTenants = isPlatformOwner || hasPermission("tenant.provision");
  const canInviteUsers = isPlatformOwner || hasPermission("user.invite");

  const { data: tenants, isLoading } = useQuery<TenantWithUserCount[]>({
    queryKey: ["/api/admin/tenants"],
    enabled: canViewTenants,
  });

  const { data: subscriptionPlans = [], isLoading: isLoadingPlans } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
    enabled: canViewTenants,
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({ tenantId, planId }: { tenantId: string; planId: string | null }) => {
      return await apiRequest("PATCH", `/api/admin/tenants/${tenantId}/plan`, { planId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] });
      toast({
        title: "Success",
        description: "Pricing plan updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update pricing plan",
        variant: "destructive",
      });
    },
  });

  const createTenantMutation = useMutation({
    mutationFn: async (name: string) =>
      apiRequest("POST", "/api/admin/tenants", { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] });
      toast({ title: "Tenant created" });
    },
    onError: (error: any) => toast({
      title: "Could not create tenant",
      description: error.message,
      variant: "destructive",
    }),
  });

  const inviteUserMutation = useMutation({
    mutationFn: async ({ tenantId, email }: { tenantId: string; email: string }) =>
      apiRequest("POST", "/api/user-invitations", {
        tenantId,
        email,
        role: "user",
        invitationType: "member",
      }),
    onSuccess: () => toast({ title: "Invitation sent" }),
    onError: (error: any) => toast({
      title: "Could not send invitation",
      description: error.message,
      variant: "destructive",
    }),
  });

  const createTenant = () => {
    const name = window.prompt("Tenant name")?.trim();
    if (name) createTenantMutation.mutate(name);
  };

  const inviteUser = (tenantId: string) => {
    const email = window.prompt("Email address")?.trim();
    if (email) inviteUserMutation.mutate({ tenantId, email });
  };

  if (permissionsLoading) {
    return <div className="p-6"><Card className="p-6"><p className="text-muted-foreground">Loading permissions…</p></Card></div>;
  }

  if (!canViewTenants) {
    return (
      <div className="p-6">
        <Card className="p-6">
          <p className="text-muted-foreground">Access denied. Tenant access is required.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="w-8 h-8" />
            Platform Operations
          </h1>
          <p className="text-muted-foreground mt-2">
            Tenant operational details only. Personal user records are not shown.
          </p>
        </div>
        {canProvisionTenants && (
          <Button
            onClick={createTenant}
            disabled={createTenantMutation.isPending}
            data-testid="button-platform-create-tenant"
          >
            Create Tenant
          </Button>
        )}
      </div>

      <Card>
        <div className="p-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : tenants && tenants.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Max Goals</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                     {canInviteUsers && <TableHead>Invitation</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          {tenant.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {tenant.slug}
                        </code>
                      </TableCell>
                      <TableCell>
                        {canManageTenants ? <Select
                          value={tenant.currentPlanId || "none"}
                          onValueChange={(value) => {
                            const planId = value === "none" ? null : value;
                            updatePlanMutation.mutate({ tenantId: tenant.id, planId });
                          }}
                          disabled={isLoadingPlans || updatePlanMutation.isPending}
                        >
                          <SelectTrigger 
                            className="w-[200px]" 
                            data-testid={`select-plan-${tenant.id}`}
                          >
                            <SelectValue placeholder="Select plan" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Plan (Default)</SelectItem>
                            {subscriptionPlans.map((plan) => (
                              <SelectItem key={plan.id} value={plan.id}>
                                {plan.displayName} - ${(plan.price / 100).toFixed(2)}/{plan.interval}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select> : <span className="text-sm">{tenant.planType || "No plan"}</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          {tenant.userCount} / {tenant.maxUsers}
                        </div>
                      </TableCell>
                       {canInviteUsers && (
                         <TableCell>
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => inviteUser(tenant.id)}
                             disabled={inviteUserMutation.isPending}
                             data-testid={`button-invite-user-${tenant.id}`}
                           >
                             Invite Member
                           </Button>
                         </TableCell>
                       )}
                      <TableCell>{tenant.maxGoals}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {tenant.isActive ? (
                            <>
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                              <span className="text-green-600 dark:text-green-400">Active</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 text-red-500" />
                              <span className="text-red-600 dark:text-red-400">Inactive</span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {tenant.createdAt ? format(new Date(tenant.createdAt), "MMM d, yyyy") : "—"}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No tenants found</p>
            </div>
          )}
        </div>
      </Card>

      {tenants && tenants.length > 0 && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-muted p-4 rounded-lg">
              <div className="text-2xl font-bold">{tenants.length}</div>
              <div className="text-sm text-muted-foreground">Total Tenants</div>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <div className="text-2xl font-bold">
                {tenants.filter(t => t.isActive).length}
              </div>
              <div className="text-sm text-muted-foreground">Active Tenants</div>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <div className="text-2xl font-bold">
                {tenants.reduce((sum, t) => sum + t.userCount, 0)}
              </div>
              <div className="text-sm text-muted-foreground">Total Users</div>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <div className="text-2xl font-bold">
                {Math.round(tenants.reduce((sum, t) => sum + t.userCount, 0) / tenants.length * 10) / 10}
              </div>
              <div className="text-sm text-muted-foreground">Avg Users/Tenant</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
